/** The canonical Partners leaves and the complete legacy URL map (TMMPDM-0058). */
/**
 * TMMPDM-0088: the partner page in three jobs — Overview (what needs you, then the partner at a
 * glance), Reports (what is happening, over time) and Settings (how this partner is set up).
 * Reports and Settings group their leaves in a side menu. Leaf keys are unchanged; `details` and
 * `cardOnFile` are new. TMMPDM-0095: Reports › Money opens with `ledger`, the partner ledger.
 * TMMPDM-0100: Notices sent is its own leaf, and the ledger is three leaves (balance, statements,
 * unassigned lines).
 */
export const SECTIONS = {
  overview: ['overview'],
  reports: [
    'standing',
    'notices',
    'billing',
    'ledger',
    'ledgerStatements',
    'ledgerUnassigned',
    'settlements',
    'revenue',
    'affiliatePayouts',
    'subscriptions',
    'statements',
    'seats',
    'aiUsage',
    'deletions',
    'affiliates',
  ],
  settings: [
    'agreement',
    'priceCard',
    'offers',
    'promotions',
    'addons',
    'commissionPlans',
    'details',
    'partnerAdministrators',
    'customers',
    'domain',
    'ghl',
    'payments',
    'cardOnFile',
  ],
} as const;
export type SectionKey = keyof typeof SECTIONS;
export type TabKey = (typeof SECTIONS)[SectionKey][number];
export const SECTION_KEYS = Object.keys(SECTIONS) as SectionKey[];
export const SECTION_LABELS: Record<SectionKey, string> = {
  overview: 'Overview',
  reports: 'Reports',
  settings: 'Settings',
};
export const CAPTIONS: Record<SectionKey, string> = {
  overview: 'What needs you, and how is this partner doing?',
  reports: "What's happening, over time.",
  settings: 'How this partner is set up.',
};

/** The side menu's groups, in order. Each section's groups, concatenated, are its leaves. */
export const GROUPS = {
  reports: [
    { key: 'standing', tabs: ['standing', 'notices', 'billing'] },
    {
      key: 'money',
      tabs: [
        'ledger',
        'ledgerStatements',
        'ledgerUnassigned',
        'settlements',
        'revenue',
        'affiliatePayouts',
      ],
    },
    { key: 'subscribers', tabs: ['subscriptions', 'statements', 'seats', 'aiUsage', 'deletions'] },
    { key: 'affiliates', tabs: ['affiliates'] },
  ],
  settings: [
    { key: 'terms', tabs: ['agreement'] },
    { key: 'pricing', tabs: ['priceCard', 'offers', 'promotions', 'addons', 'commissionPlans'] },
    { key: 'profile', tabs: ['details'] },
    { key: 'access', tabs: ['partnerAdministrators', 'customers'] },
    { key: 'connections', tabs: ['domain', 'ghl', 'payments', 'cardOnFile'] },
  ],
} as const satisfies Record<
  'reports' | 'settings',
  readonly { key: string; tabs: readonly TabKey[] }[]
>;
export type GroupKey = (typeof GROUPS)[keyof typeof GROUPS][number]['key'];
export const GROUP_KEYS = [
  ...new Set(Object.values(GROUPS).flatMap((groups) => groups.map((group) => group.key))),
] as GroupKey[];
export const GROUP_LABELS: Record<GroupKey, string> = {
  standing: 'Standing',
  money: 'Money',
  subscribers: 'Subscribers',
  affiliates: 'Affiliates',
  terms: 'Terms',
  pricing: 'Pricing',
  profile: 'Profile',
  access: 'Access',
  connections: 'Connections',
};
export const TAB_LABELS: Record<TabKey, string> = {
  overview: 'Overview',
  agreement: 'Agreement',
  standing: 'Standing',
  notices: 'Notices sent',
  billing: 'Charges',
  ledger: 'Balance and holds',
  ledgerStatements: 'Statements',
  ledgerUnassigned: 'Unassigned lines',
  settlements: 'Settlements',
  priceCard: 'Rate card',
  offers: 'Offers',
  promotions: 'Promotions',
  addons: 'Add-ons',
  subscriptions: 'Subscriptions',
  statements: 'Statements',
  seats: 'Seats',
  aiUsage: 'AI usage',
  revenue: 'Revenue',
  deletions: 'Deletions',
  affiliates: 'Affiliates',
  commissionPlans: 'Commission plans',
  affiliatePayouts: 'Affiliate payouts',
  partnerAdministrators: 'Partner administrators',
  customers: 'Customers',
  domain: 'Domain',
  ghl: 'GHL connection',
  payments: 'Payments',
  details: 'Details and brand',
  cardOnFile: 'Card on file',
};
export const LEGACY_TABS = {
  reseller: {
    overview: 'overview',
    agreement: 'agreement',
    standing: 'standing',
    billing: 'billing',
    settlements: 'settlements',
    tenants: 'overview',
    customerPrices: 'priceCard',
    affiliateCommissions: 'commissionPlans',
    administrators: 'partnerAdministrators',
    rateCards: 'priceCard',
    commissionPlans: 'commissionPlans',
    affiliates: 'commissionPlans',
    users: 'partnerAdministrators',
    invitations: 'partnerAdministrators',
    notices: 'notices',
    payouts: 'settlements',
    ghl: 'ghl',
  },
  tenant: {
    overview: 'overview',
    domain: 'domain',
    reseller: 'overview',
    offers: 'offers',
    customerPriceCard: 'priceCard',
    promotions: 'promotions',
    addons: 'addons',
    revenue: 'revenue',
    payouts: 'affiliatePayouts',
    subscriptions: 'subscriptions',
    statements: 'statements',
    seats: 'seats',
    aiUsage: 'aiUsage',
    deletions: 'deletions',
    administrators: 'partnerAdministrators',
    customers: 'customers',
    invitations: 'customers',
    affiliates: 'affiliates',
    pricing: 'priceCard',
    rateCard: 'priceCard',
    agreement: 'agreement',
    members: 'partnerAdministrators',
  },
} as const satisfies Record<string, Record<string, TabKey>>;
export function legacyTab(kind: keyof typeof LEGACY_TABS, key: string | null): TabKey {
  const map: Record<string, TabKey> = LEGACY_TABS[kind];
  return key !== null && Object.hasOwn(map, key) ? (map[key] ?? 'overview') : 'overview';
}
// Dormant (TMMPDM-0085): hidden from People; not used in the current model.
export const DORMANT_TENANT_ADMINISTRATORS = 'tenantAdministrators' as const;

export function resolveTab(value: string | null): TabKey {
  if (value === 'invitations') return 'customers';
  if (value === 'tenantAdministrators') return 'partnerAdministrators';
  return (
    SECTION_KEYS.flatMap((key) => [...SECTIONS[key]]).find((tab) => tab === value) ?? 'overview'
  );
}
export function sectionOf(tab: TabKey): SectionKey {
  return (
    SECTION_KEYS.find((key) => (SECTIONS[key] as readonly string[]).includes(tab)) ?? 'overview'
  );
}
export function firstTab(section: SectionKey): TabKey {
  return SECTIONS[section][0];
}
export function hasLocalNav(section: SectionKey): boolean {
  return section !== 'overview';
}
